import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";
import { useT } from "../i18n/LanguageContext";
import { track } from "../lib/analytics";

const SITE_URL = "https://gfirem.com";

/**
 * Adjusts <head> so bots and share cards treat this route as a 404 while
 * humans still get a polished landing page. React Router serves this on the
 * client, and `scripts/prerender-seo.js` emits a matching static build/404.html
 * so static hosts can return the file with HTTP 404 for direct hits.
 */
function useNotFoundHead(t, lang) {
    useEffect(() => {
        const title = `404 · ${t("notFound.title")} | gfirem`;
        const description = t("notFound.body");
        const canonical = `${SITE_URL}/404`;

        const prev = {
            title: document.title,
            description:
                document.head
                    .querySelector('meta[name="description"]')
                    ?.getAttribute("content") ?? null,
            canonical:
                document.head
                    .querySelector('link[rel="canonical"]')
                    ?.getAttribute("href") ?? null,
            robots:
                document.head
                    .querySelector('meta[name="robots"]')
                    ?.getAttribute("content") ?? null,
        };

        document.title = title;

        const setMeta = (selector, attr, value) => {
            const el = document.head.querySelector(selector);
            if (el) el.setAttribute(attr, value);
        };
        const ensureMeta = (name, value) => {
            let el = document.head.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", value);
            return el;
        };

        setMeta('meta[name="description"]', "content", description);
        setMeta('link[rel="canonical"]', "href", canonical);
        // Tell search engines not to index a 404.
        const robotsEl = ensureMeta("robots", "noindex, follow");

        return () => {
            document.title = prev.title;
            if (prev.description !== null) {
                setMeta('meta[name="description"]', "content", prev.description);
            }
            if (prev.canonical !== null) {
                setMeta('link[rel="canonical"]', "href", prev.canonical);
            }
            if (prev.robots !== null) {
                robotsEl.setAttribute("content", prev.robots);
            } else {
                robotsEl.parentNode?.removeChild(robotsEl);
            }
        };
    }, [t, lang]);
}

export default function NotFound() {
    const { t, lang } = useT();
    const location = useLocation();

    useNotFoundHead(t, lang);

    // Log the bad URL once per mount so we can spot dead links in analytics.
    useEffect(() => {
        track("not_found_viewed", {
            path: location.pathname + location.search,
            referrer: document.referrer || null,
            lang,
        });
    }, [location.pathname, location.search, lang]);

    return (
        <main className="min-h-screen bg-[var(--sand)] text-[var(--ink)]" data-testid="not-found-page">
            <Nav />

            <section className="mx-auto max-w-6xl px-6 pt-24 sm:pt-32 pb-24">
                <p
                    className="text-xs sm:text-sm tracking-[0.2em] uppercase text-[var(--ink)]/60 border-t border-b border-[var(--ink)]/20 py-2 inline-block"
                    data-testid="not-found-overline"
                >
                    {t("notFound.overline")}
                </p>

                <div
                    className="mt-10 sm:mt-14 flex items-end gap-6 sm:gap-10"
                    aria-hidden
                >
                    <span
                        className="font-serif text-[7rem] sm:text-[11rem] leading-none text-[var(--accent)]"
                        data-testid="not-found-code"
                    >
                        {t("notFound.code")}
                    </span>
                </div>

                <h1
                    className="mt-6 sm:mt-8 font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl"
                    data-testid="not-found-title"
                >
                    {t("notFound.title")}
                </h1>

                <p
                    className="mt-6 text-base sm:text-lg text-[var(--ink)]/80 max-w-2xl leading-relaxed"
                    data-testid="not-found-body"
                >
                    {t("notFound.body")}
                </p>

                <div className="mt-10 flex flex-wrap gap-3 sm:gap-4" data-testid="not-found-actions">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 bg-[var(--ink)] text-[var(--sand)] px-6 py-3 hover:opacity-90 transition-opacity"
                        data-cta-source="not-found-home"
                        data-testid="not-found-cta-home"
                    >
                        {t("notFound.ctaHome")}
                        <span aria-hidden>→</span>
                    </Link>
                    <Link
                        to="/case-studies"
                        className="inline-flex items-center gap-2 border border-[var(--ink)] px-6 py-3 hover:bg-[var(--ink)] hover:text-[var(--sand)] transition-colors"
                        data-cta-source="not-found-work"
                        data-testid="not-found-cta-work"
                    >
                        {t("notFound.ctaWork")}
                        <span aria-hidden>→</span>
                    </Link>
                    <Link
                        to="/#contact"
                        className="inline-flex items-center gap-2 px-6 py-3 underline underline-offset-4 hover:no-underline"
                        data-cta-source="not-found-contact"
                        data-testid="not-found-cta-contact"
                    >
                        {t("notFound.ctaContact")}
                    </Link>
                </div>

                <p
                    className="mt-16 font-mono text-xs text-[var(--ink)]/50 break-all"
                    data-testid="not-found-path"
                >
                    {location.pathname}
                </p>
            </section>

            <Footer />
        </main>
    );
}
